import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { ActionService } from '../../shared/_services/action.service';
import { ActionResponse } from '../../shared/_models/action-response.model';
import { DSTSharedService } from '../shared/dst-shared.service';

/** Action IDs for DST Statements Filed Status (match AngularJS DstStatementsFiledStatusFactory). */
const ACTION_TAX_YEARS = 32172;
const ACTION_BUSINESSES = 32642;
const ACTION_GRID_DATA = 32643;
const ACTION_UPDATE_STATUS = 32644;

/**
 * Service for DST Statements Filed Status screen.
 * Loads tax years, businesses, grid data, and updates statement filed/QAR status.
 */
@Injectable({
  providedIn: 'root'
})
export class StatementsFiledStatusService {

  constructor(
    private http: HttpClient,
    private actionService: ActionService,
    private dstShared: DSTSharedService
  ) {}

  /** Load tax years (action_id 32172). */
  getTaxYears(): Observable<{ text: string }[]> {
    const params = new HttpParams().set('sso_id', this.dstShared.user?.ssoId ?? '');
    return this.actionService
      .getActionData(this.dstShared.baseURLs.api, ACTION_TAX_YEARS, this.dstShared.clientKey, params)
      .pipe(
        map((res: ActionResponse) => res.callSuccess === '1' ? (res.jsonObject || []) : [])
      );
  }

  /** Load businesses for the given tax year (action_id 32642). */
  getBusinesses(taxYear: string): Observable<{ text: string; id: string | number }[]> {
    const params = new HttpParams()
      .set('sso_id', this.dstShared.user?.ssoId ?? '')
      .set('tax_year', taxYear);
    return this.actionService
      .getActionData(this.dstShared.baseURLs.api, ACTION_BUSINESSES, this.dstShared.clientKey, params)
      .pipe(
        map((res: ActionResponse) => res.callSuccess === '1' ? (res.jsonObject || []) : [])
      );
  }

  /** Load grid data for statements filed status (action_id 32643). */
  getGridData(taxYear: string, businessId: string | number): Observable<any[]> {
    const params = new HttpParams()
      .set('tax_year', taxYear)
      .set('business_id', String(businessId));
    return this.actionService
      .getActionData(this.dstShared.baseURLs.api, ACTION_GRID_DATA, this.dstShared.clientKey, params)
      .pipe(
        map((res: ActionResponse) => res.callSuccess === '1' ? (res.jsonObject || []) : [])
      );
  }

  /**
   * Update statement filed status or QAR status (action_id 32644).
   * Params built explicitly per ANGULAR_RULE_MIGRATION §3.10.
   */
  updateStatementFiledStatus(
    statementIds: string,
    inStatusType: string,
    inType: string
  ): Observable<{ callSuccess: string; errorMessage?: string }> {
    const baseUrl = this.dstShared.baseURLs.api;
    const url = `${baseUrl}/saveJsonObject?action_id=${ACTION_UPDATE_STATUS}`;
    const params = new HttpParams()
      .set('statement_ids', statementIds)
      .set('in_status_type', inStatusType)
      .set('in_type', inType)
      .set('tax_year', this.dstShared.gtwTaxYear ?? '')
      .set('scenario', this.dstShared.gtwScenario ?? '')
      .set('jcd_key', '500') // fallback; use globalParams when available
      .set('scenarioCode', 'RAF')
      .set('is_issue_key_enabled', 'N');
    return this.http.post<{ callSuccess: string; errorMessage?: string }>(url, params.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      withCredentials: true
    });
  }
}
